import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {forkJoin, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from 'environments/environment';
import {WarehouseService} from '../../../../../services/warehouse/warehouse.service';
import {Entrepot} from '../../../../../models/entrepot';
import {
    EntrepotFormPayload,
    LogicielCaisseEntrepotService,
} from '../../../../../services/logiciel-caisse/logiciel-caisse-entrepot.service';
import {LogicielCaisseAuthService} from '../../../../../services/logiciel-caisse/logiciel-caisse-auth.service';

@Component({
    selector: 'app-edit-warehouse',
    templateUrl: './edit-warehouse.component.html',
    styleUrls: ['./edit-warehouse.component.scss']
})
export class EditWarehouseComponent implements OnInit {

    @ViewChild('modalLG') modalTemplate!: TemplateRef<any>;
    @Input() warehouseData: any = null;
    @Input() warehouse: any;
    @Output() save = new EventEmitter<any>();
    @Output() cancel = new EventEmitter<void>();
    /** Réponse API création / mise à jour (contient en général `id`) — utile pour sélectionner l’entrepôt dans les formulaires. */
    @Output() warehouseSaved = new EventEmitter<any>();

    constructor(
        private warehouseService: WarehouseService,
        private lcEntrepotService: LogicielCaisseEntrepotService,
        private lcAuth: LogicielCaisseAuthService,
        private modalService: NgbModal,
        private toastr: ToastrService,
    ) {}

    currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    clientId = this.currentUser?.id;
    stationId = this.currentUser?.stations?.[0]?.id;
    private modalRef!: NgbModalRef;
    public loading = false;

    entrepot: Entrepot = {
        id: 0,
        user_id: 0,
        station_id: 0,
        nom: '',
        code: '',
        adresse: '',
        ville: '',
        code_postal: '',
        pays: ''
    };

    dataError: {
        nom: string;
        code: string;
        adresse: string;
        ville: string;
        code_postal: string;
        pays: string;
    } = {
        nom: '',
        code: '',
        adresse: '',
        ville: '',
        code_postal: '',
        pays: '',
    };

    ngOnInit(): void {
    }

    openModal() {
        if (!this.warehouseData) {
            this.resetForm();
        } else {
            this.entrepot = {
                nom: this.warehouseData.nom,
                code: this.warehouseData.code ?? this.warehouseData.code_entrepot ?? '',
                adresse: this.warehouseData.adresse,
                ville: this.warehouseData.ville,
                code_postal: this.warehouseData.code_postal,
                pays: this.warehouseData.pays
            };
        }
        const options: NgbModalOptions = {
            centered: true,
            backdrop: 'static',
            keyboard: true,
            size: 'lg',
        };
        this.loading = false;
        this.modalRef = this.modalService.open(this.modalTemplate, options);
    }

    onSubmit(): void {
        if (this.loading) {
            return;
        }

        const payload = {
            station_id: this.stationId,
            user_id: this.clientId,
            nom: this.entrepot.nom.trim(),
            code: (this.entrepot.code || '').toString().trim(),
            adresse: this.entrepot.adresse,
            ville: this.entrepot.ville,
            code_postal: this.entrepot.code_postal,
            pays: this.entrepot.pays,
        };

        if (this.warehouseData?.id) {
            if (!this.entrepot.nom?.trim() || !this.entrepot.adresse?.trim() ||
                !this.entrepot.ville?.trim() || !this.entrepot.code_postal?.trim() ||
                !this.entrepot.pays?.trim()) {
                this.toastr.warning('Tous les champs obligatoires doivent être remplis', 'Champs manquants');
                return;
            }

            this.loading = true;
            this.warehouseService.updateWarehouse(this.warehouseData.id, this.clientId, this.stationId, payload).subscribe({
                next: (res) => {
                    this.loading = false;
                    this.modalRef.close();
                    this.warehouseSaved.emit(res ?? { id: this.warehouseData.id });
                    this.toastr.success('Modification enregistrée avec succès !', '', {
                        positionClass: 'toast-bottom-right',
                        toastClass: 'toast ngx-toastr custom-success-toast',
                    });
                },
                error: (err) => {
                    this.loading = false;
                    console.error('Update warehouse error:', err);
                    this.toastr.error('Erreur lors de la modification de l\'entrepôt', 'Erreur');
                }
            });
        } else {
            this.dataError = {
                nom: '',
                code: '',
                adresse: '',
                ville: '',
                code_postal: '',
                pays: '',
            };

            let hasError = false;

            if (!this.entrepot.nom || !this.entrepot.nom.trim()) {
                this.dataError.nom = 'Nom de l\'entrepôt est requis !';
                hasError = true;
            }
            if (!this.entrepot.code || !this.entrepot.code.trim()) {
                this.dataError.code = 'Code entrepôt est requis !';
                hasError = true;
            }
            if (!this.entrepot.adresse || !this.entrepot.adresse.trim()) {
                this.dataError.adresse = 'Adresse de l\'entrepôt est requis !';
                hasError = true;
            }
            if (!this.entrepot.ville || !this.entrepot.ville.trim()) {
                this.dataError.ville = 'Ville est requis !';
                hasError = true;
            }
            if (!this.entrepot.code_postal || !this.entrepot.code_postal.trim()) {
                this.dataError.code_postal = 'Code postal est requis !';
                hasError = true;
            }
            if (!this.entrepot.pays || !this.entrepot.pays.trim()) {
                this.dataError.pays = 'Pays est requis !';
                hasError = true;
            }

            if (hasError) {
                return;
            }

            this.createWarehouseOnErpAndLc(payload);
        }
    }

    /**
     * Création double :
     * - ERP : POST {stock_url}/api/entrepots
     * - LC  : POST {api_logiciel_caisse}/api/entrepots/entrepot
     */
    private createWarehouseOnErpAndLc(erpPayload: {
        station_id: number;
        user_id: number;
        nom: string;
        code: string;
        adresse: string;
        ville: string;
        code_postal: string;
        pays: string;
    }): void {
        const erpUrl = `${String(environment.stock_url || '').replace(/\/$/, '')}/api/entrepots`;
        const lcUrl = `${String(this.lcAuth.apiBase() || environment.api_logiciel_caisse || '').replace(/\/$/, '')}/api/entrepots/entrepot`;
        const lcPayload: EntrepotFormPayload = {
            libelle: erpPayload.nom,
            ville: erpPayload.ville?.trim() || '',
            pays: erpPayload.pays?.trim() || '',
            code_entrepot: erpPayload.code || null,
            is_reassort: false,
            is_globale: false,
        };

        console.log('[Ajout entrepôt] POST ERP', erpUrl, erpPayload);
        console.log('[Ajout entrepôt] POST LC', lcUrl, lcPayload);

        this.loading = true;

        forkJoin({
            erp: this.warehouseService.addWarehouse(this.stationId, erpPayload as Entrepot).pipe(
                map((res) => ({ ok: true as const, res })),
                catchError((err) => {
                    console.error('[Ajout entrepôt] ERP erreur', err);
                    return of({ ok: false as const, err });
                })
            ),
            lc: this.lcEntrepotService.createEntrepot(lcPayload).pipe(
                map((res) => ({ ok: true as const, res })),
                catchError((err) => {
                    console.error('[Ajout entrepôt] LC erreur', err);
                    return of({ ok: false as const, err });
                })
            ),
        }).subscribe(({ erp, lc }) => {
            this.loading = false;
            console.log('[Ajout entrepôt] réponse ERP', erp);
            console.log('[Ajout entrepôt] réponse LC', lc);

            if (!erp.ok && !lc.ok) {
                this.toastr.error('Erreur lors de l\'ajout de l\'entrepôt (ERP et logiciel caisse)', 'Erreur');
                return;
            }

            this.modalRef.close();

            const erpRes = erp.ok ? erp.res : null;
            const lcRes = lc.ok ? lc.res : null;

            this.warehouseSaved.emit({
                ...(erpRes && typeof erpRes === 'object' ? erpRes : {}),
                ...(lcRes && typeof lcRes === 'object' ? { ...lcRes } : {}),
                id:
                    (lcRes as any)?.id ??
                    (erpRes as any)?.id ??
                    (erpRes as any)?.data?.id ??
                    null,
                erp_id:
                    (erpRes as any)?.id ??
                    (erpRes as any)?.data?.id ??
                    null,
                erp_entrepot: erpRes,
                lc_entrepot: lcRes,
            });

            if (erp.ok && lc.ok) {
                this.toastr.success('Ajout enregistré avec succès !', '', {
                    positionClass: 'toast-bottom-right',
                    toastClass: 'toast ngx-toastr custom-success-toast',
                });
            } else if (erp.ok) {
                this.toastr.warning(
                    'Entrepôt créé sur ERP, mais échec côté logiciel caisse (voir console).',
                    'Ajout partiel'
                );
            } else {
                this.toastr.warning(
                    'Entrepôt créé sur logiciel caisse, mais échec côté ERP (voir console).',
                    'Ajout partiel'
                );
            }
        });
    }

    private resetForm() {
        this.entrepot = {
            nom: '',
            code: '',
            adresse: '',
            ville: '',
            code_postal: '',
            pays: ''
        };
        this.warehouseData = null;
    }

    clearFieldError(fieldName: string): void {
        if (this.dataError && this.dataError[fieldName]) {
            this.dataError[fieldName] = '';
        }
    }
}
